import type { Graph, Edge } from '@antv/x6';

export const MERGE_SNAP_RADIUS = 16;

export function isInvocationGuideEdge(edge: any): boolean {
  const data = edge?.getData?.() || {};
  return !!data._invocationAngleGuide;
}

export function getInvocationEdgeStartPoint(edge: Edge): { x: number; y: number } | null {
  const src = edge.getSource?.() as any;
  if (typeof src?.x === 'number') return { x: src.x, y: src.y };
  const sourceCell = edge.getSourceCell?.();
  const targetCell = edge.getTargetCell?.();
  if (sourceCell?.isNode?.() && targetCell?.isNode?.()) {
    const data = edge.getData?.() || {};
    const sBox = sourceCell.getBBox();
    const sCx = sBox.x + sBox.width / 2;
    const sCy = sBox.y + sBox.height / 2;
    if (data._fixedStartOffset && typeof data._fixedStartOffset.dx === 'number') {
      return { x: sCx + data._fixedStartOffset.dx, y: sCy + data._fixedStartOffset.dy };
    }
    const tBox = targetCell.getBBox();
    const tCx = tBox.x + tBox.width / 2;
    const tCy = tBox.y + tBox.height / 2;
    const dx = tCx - sCx;
    const dy = tCy - sCy;
    const hw = sBox.width / 2;
    const hh = sBox.height / 2;
    const scaleX = dx !== 0 ? hw / Math.abs(dx) : Infinity;
    const scaleY = dy !== 0 ? hh / Math.abs(dy) : Infinity;
    const scale = Math.min(scaleX, scaleY);
    return { x: sCx + dx * scale, y: sCy + dy * scale };
  }
  return null;
}

export function getMergeCandidates(graph: Graph, dragEdge: Edge): Edge[] {
  const dragSourceId = dragEdge.getSourceCellId?.() || (dragEdge.getData?.()?._invocationSourceNodeId);
  return graph.getEdges().filter(edge => {
    if (edge.id === dragEdge.id) return false;
    if (isInvocationGuideEdge(edge)) return false;
    const data = edge.getData?.() || {};
    if (data.edgeSubtype !== 'invocation') return false;
    const sourceId = edge.getSourceCellId?.() || data._invocationSourceNodeId;
    return sourceId && sourceId === dragSourceId;
  });
}

export function findMergeSnapTarget(
  graph: Graph,
  dragEdge: Edge,
  pos: { x: number; y: number },
): { x: number; y: number; edgeId: string } | null {
  const candidates = getMergeCandidates(graph, dragEdge);
  let best: { x: number; y: number; edgeId: string; dist: number } | null = null;
  for (const edge of candidates) {
    const start = getInvocationEdgeStartPoint(edge);
    if (!start) continue;
    const dist = Math.hypot(start.x - pos.x, start.y - pos.y);
    if (dist <= MERGE_SNAP_RADIUS && (!best || dist < best.dist)) {
      best = { x: start.x, y: start.y, edgeId: edge.id, dist };
    }
  }
  return best ? { x: best.x, y: best.y, edgeId: best.edgeId } : null;
}

export function getSvgViewport(graph: Graph): SVGElement | null {
  return (graph.view as any).stage || null;
}

export function showMergeSnapIndicator(
  graph: Graph,
  pos: { x: number; y: number },
  indicatorRef: { current: SVGElement | null },
): void {
  const viewport = getSvgViewport(graph);
  if (!viewport) return;
  if (indicatorRef.current) {
    indicatorRef.current.remove();
    indicatorRef.current = null;
  }
  const ns = 'http://www.w3.org/2000/svg';
  const g = document.createElementNS(ns, 'g');
  const outer = document.createElementNS(ns, 'circle');
  outer.setAttribute('cx', String(pos.x));
  outer.setAttribute('cy', String(pos.y));
  outer.setAttribute('r', '10');
  outer.setAttribute('fill', '#1890ff');
  outer.setAttribute('fill-opacity', '0.3');
  outer.setAttribute('stroke', '#1890ff');
  outer.setAttribute('stroke-width', '2');
  outer.setAttribute('stroke-dasharray', '3 2');
  const inner = document.createElementNS(ns, 'circle');
  inner.setAttribute('cx', String(pos.x));
  inner.setAttribute('cy', String(pos.y));
  inner.setAttribute('r', '5');
  inner.setAttribute('fill', '#1890ff');
  g.appendChild(outer);
  g.appendChild(inner);
  g.style.pointerEvents = 'none';
  viewport.appendChild(g);
  indicatorRef.current = g as unknown as SVGElement;
}

export function hideMergeSnapIndicator(indicatorRef: { current: SVGElement | null }): void {
  if (indicatorRef.current) {
    indicatorRef.current.remove();
    indicatorRef.current = null;
  }
}

export function showAllMergeHighlights(
  graph: Graph,
  dragEdge: Edge,
  highlightRef: { current: SVGElement | null },
): void {
  const viewport = getSvgViewport(graph);
  if (!viewport) return;
  hideAllMergeHighlights(highlightRef);
  const candidates = getMergeCandidates(graph, dragEdge);
  if (!candidates.length) return;
  const ns = 'http://www.w3.org/2000/svg';
  const g = document.createElementNS(ns, 'g');
  candidates.forEach(edge => {
    const start = getInvocationEdgeStartPoint(edge);
    if (!start) return;
    const ring = document.createElementNS(ns, 'circle');
    ring.setAttribute('cx', String(start.x));
    ring.setAttribute('cy', String(start.y));
    ring.setAttribute('r', '8');
    ring.setAttribute('fill', '#1890ff');
    ring.setAttribute('fill-opacity', '0.15');
    ring.setAttribute('stroke', '#1890ff');
    ring.setAttribute('stroke-width', '1.5');
    ring.setAttribute('stroke-dasharray', '2 2');
    g.appendChild(ring);
  });
  g.style.pointerEvents = 'none';
  viewport.appendChild(g);
  highlightRef.current = g as unknown as SVGElement;
}

export function hideAllMergeHighlights(highlightRef: { current: SVGElement | null }): void {
  if (highlightRef.current) {
    highlightRef.current.remove();
    highlightRef.current = null;
  }
}
